import React, { useState, useEffect, useContext, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { Colorize } from "@material-ui/icons";
import { ColorBox } from "material-ui-color";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  colorAdorment: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorPickerWrapper: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
  },
}));

const TagSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Mensagem muito curta")
    .required("Obrigatório")
});

const TagModal = ({ open, onClose, tagId, kanban }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  // Manter os valores iniciais estáveis: uma escolha de cor não pode
  // reinicializar o Formik e apagar o nome já digitado.
  const initialState = useMemo(() => ({
    name: "",
    color: getRandomHexColor(),
  }), []);

  const [tag, setTag] = useState(initialState);

  useEffect(() => {
    const loadTag = async () => {
      try {
        if (tagId) {
          const { data } = await api.get(`/tags/${tagId}`);
          setTag(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      setTag(initialState);
      loadTag();
    }
  }, [tagId, open]);

  const handleClose = () => {
    setTag(initialState);
    setColorPickerModalOpen(false);
    onClose();
  };

  const handleSaveTag = async values => {
    const tagData = { ...values, userId: user?.id, kanban };

    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, tagData);
      } else {
        await api.post("/tags", tagData);
      }
      toast.success(kanban === 0 ? i18n.t("tagModal.success") : i18n.t("tagModal.successKanban"));
    } catch (err) {
      toastError(err);
    }

    handleClose();
  };

  function getRandomHexColor() {
    const r = Math.floor(Math.random() * 256).toString(16).padStart(2, "0");
    const g = Math.floor(Math.random() * 256).toString(16).padStart(2, "0");
    const b = Math.floor(Math.random() * 256).toString(16).padStart(2, "0");
    return `#${r}${g}${b}`;
  }

  return (
    <div className={classes.root}>
     <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">

        <DialogTitle>
          {tagId
            ? kanban === 0
              ? i18n.t("tagModal.title.edit")
              : i18n.t("tagModal.title.editKanban")
            : kanban === 0
            ? i18n.t("tagModal.title.add")
            : i18n.t("tagModal.title.addKanban")}
        </DialogTitle>

        <Formik
          initialValues={tag}
          enableReinitialize
          validationSchema={TagSchema}
          onSubmit={(values, actions) => {
            handleSaveTag(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("tagModal.form.name")}
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />

                <Field
                  as={TextField}
                  label={i18n.t("tagModal.form.color")}
                  name="color"
                  id="color"
                  error={touched.color && Boolean(errors.color)}
                  helperText={touched.color && errors.color}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <div
                          style={{ backgroundColor: values.color }}
                          className={classes.colorAdorment}
                        ></div>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
                      >
                        <Colorize />
                      </IconButton>
                    ),
                  }}
                />

                {colorPickerModalOpen && (
                  <div className={classes.colorPickerWrapper}>
                    <ColorBox
                      disableAlpha
                      hslGradient={false}
                      value={values.color}
                      onChange={val => {
                        setFieldValue("color", `#${val.hex}`, false);
                      }}
                    />
                  </div>
                )}
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("tagModal.buttons.cancel")}
                </Button>
                <div className={classes.btnWrapper}>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                  >
                    {tagId
                      ? i18n.t("tagModal.buttons.okEdit")
                      : i18n.t("tagModal.buttons.okAdd")}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TagModal;
